import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Building2, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { bankingApi, BankAccount } from '../lib/api/banking';
import { getCompanyId } from '../lib/config';

interface BankAccountFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  companyId?: string;
  editingAccount?: BankAccount | null;
}

export function BankAccountForm({ 
  open, 
  onOpenChange, 
  onSuccess,
  companyId,
  editingAccount
}: BankAccountFormProps) {
  const [loading, setLoading] = useState(false);
  const [manualBankName, setManualBankName] = useState('');
  const [formData, setFormData] = useState({
    bankName: '',
    accountNumber: '',
    accountType: 'checking',
    currency: 'RWF', // Default to RWF for Rwanda
    balance: '0',
    routingNumber: '',
    swiftCode: '',
    iban: '',
    accountHolder: '',
    branchCode: '',
    branchName: '',
    notes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  // Load form data when dialog opens
  useEffect(() => {
    if (open) {
      if (editingAccount) {
        const bankName = editingAccount.bankName || '';
        // Check if the bank name is in our predefined list
        const predefinedBanks = [
          'BK (Bank of Kigali)',
          'Equity Bank Rwanda',
          'Ecobank Rwanda',
          'I&M Bank Rwanda',
          'BPR Bank Rwanda',
          'Access Bank Rwanda',
          'Bank of Africa Rwanda',
          'GTBank Rwanda',
          'Cogebanque',
          'Urwego Opportunity Bank'
        ];
        
        if (predefinedBanks.includes(bankName)) {
          setFormData({
            bankName: bankName,
            accountNumber: editingAccount.accountNumber || '',
            accountType: editingAccount.accountType || 'checking',
            currency: editingAccount.currency || 'RWF',
            balance: editingAccount.balance ? String(editingAccount.balance) : '0',
            routingNumber: editingAccount.routingNumber || '',
            swiftCode: editingAccount.swiftCode || '',
            iban: editingAccount.iban || '',
            accountHolder: editingAccount.accountHolder || '',
            branchCode: editingAccount.branchCode || '',
            branchName: editingAccount.branchName || '',
            notes: editingAccount.notes || ''
          });
          setManualBankName('');
        } else {
          // If not in predefined list, set to "Other" and use manual input
          setFormData({
            bankName: 'Other',
            accountNumber: editingAccount.accountNumber || '',
            accountType: editingAccount.accountType || 'checking',
            currency: editingAccount.currency || 'RWF',
            balance: editingAccount.balance ? String(editingAccount.balance) : '0',
            routingNumber: editingAccount.routingNumber || '',
            swiftCode: editingAccount.swiftCode || '',
            iban: editingAccount.iban || '',
            accountHolder: editingAccount.accountHolder || '',
            branchCode: editingAccount.branchCode || '',
            branchName: editingAccount.branchName || '',
            notes: editingAccount.notes || ''
          });
          setManualBankName(bankName);
        }
      } else {
        resetForm();
      }
    }
  }, [open, editingAccount]);

  const resetForm = () => {
    setFormData({
      bankName: '',
      accountNumber: '',
      accountType: 'checking',
      currency: 'RWF', // Default to RWF for Rwanda
      balance: '0',
      routingNumber: '',
      swiftCode: '',
      iban: '',
      accountHolder: '',
      branchCode: '',
      branchName: '',
      notes: ''
    });
    setManualBankName('');
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.bankName || formData.bankName.trim() === '' || (formData.bankName === 'Other' && !manualBankName.trim())) {
      newErrors.bankName = 'Bank name is required';
    }
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const effectiveCompanyId = companyId || getCompanyId();
      
      // Convert balance to number, default to 0 if empty or invalid
      const balanceValue = formData.balance && formData.balance.trim() !== '' 
        ? parseFloat(formData.balance) 
        : 0;
      const balance = isNaN(balanceValue) ? 0 : balanceValue;
      
      console.log('Submitting bank account with balance:', balance, 'from formData.balance:', formData.balance);
      
      if (editingAccount) {
        // Update existing account
        const { balance: _, ...formDataWithoutBalance } = formData;
        await bankingApi.updateBankAccount(editingAccount.id, {
          ...formDataWithoutBalance,
          balance,
          companyId: effectiveCompanyId
        });
        toast({
          title: "Success",
          description: "Bank account updated successfully",
        });
      } else {
        // Create new account
        const { balance: _, ...formDataWithoutBalance } = formData;
        const accountData = {
          ...formDataWithoutBalance,
          balance,
          companyId: effectiveCompanyId
        };
        console.log('Creating bank account with data:', accountData);
        await bankingApi.createBankAccount(accountData);
        toast({
          title: "Success",
          description: "Bank account created successfully",
        });
      }
      
      resetForm();
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error saving bank account:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save bank account",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {editingAccount ? 'Edit Bank Account' : 'Create Bank Account'}
          </DialogTitle>
          <DialogDescription>
            {editingAccount ? 'Update bank account details' : 'Add a new bank account to your company'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Bank Name */}
          <div className="space-y-2">
            <Label htmlFor="bankName">Bank Name *</Label>
            <Select
              value={formData.bankName}
              onValueChange={(value) => {
                setFormData(prev => ({ ...prev, bankName: value }));
                if (value !== 'Other') {
                  setManualBankName('');
                }
              }}
              disabled={loading}
            >
              <SelectTrigger id="bankName" className="h-10 border-slate-300 w-full">
                <SelectValue placeholder="Select a bank" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BK (Bank of Kigali)">BK (Bank of Kigali)</SelectItem>
                <SelectItem value="Equity Bank Rwanda">Equity Bank Rwanda</SelectItem>
                <SelectItem value="Ecobank Rwanda">Ecobank Rwanda</SelectItem>
                <SelectItem value="I&M Bank Rwanda">I&M Bank Rwanda</SelectItem>
                <SelectItem value="BPR Bank Rwanda">BPR Bank Rwanda</SelectItem>
                <SelectItem value="Access Bank Rwanda">Access Bank Rwanda</SelectItem>
                <SelectItem value="Bank of Africa Rwanda">Bank of Africa Rwanda</SelectItem>
                <SelectItem value="GTBank Rwanda">GTBank Rwanda</SelectItem>
                <SelectItem value="Cogebanque">Cogebanque</SelectItem>
                <SelectItem value="Urwego Opportunity Bank">Urwego Opportunity Bank</SelectItem>
                <SelectItem value="Other">Other (Enter manually)</SelectItem>
              </SelectContent>
            </Select>
            {errors.bankName && (
              <p className="text-sm text-red-500">{errors.bankName}</p>
            )}
          </div>

          {/* Manual Bank Name Input (if Other is selected) */}
          {formData.bankName === 'Other' && (
            <div className="space-y-2">
              <Label htmlFor="manualBankName">Enter Bank Name *</Label>
              <Input
                id="manualBankName"
                value={manualBankName}
                onChange={(e) => {
                  setManualBankName(e.target.value);
                  setFormData(prev => ({ ...prev, bankName: e.target.value }));
                }}
                placeholder="Enter bank name"
                disabled={loading}
                className="h-10 border-slate-300"
              />
              {errors.bankName && (
                <p className="text-sm text-red-500">{errors.bankName}</p>
              )}
            </div>
          )}

          {/* Account Number */}
          <div className="space-y-2">
            <Label htmlFor="accountNumber">Account Number *</Label>
            <Input
              id="accountNumber"
              value={formData.accountNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, accountNumber: e.target.value }))}
              placeholder="Enter account number"
              disabled={loading}
              className="h-10 border-slate-300"
            />
            {errors.accountNumber && (
              <p className="text-sm text-red-500">{errors.accountNumber}</p>
            )}
          </div>

          {/* Initial Balance */}
          <div className="space-y-2">
            <Label htmlFor="balance">Initial Balance (Optional)</Label>
            <Input
              id="balance"
              type="number"
              step="0.01"
              value={formData.balance}
              onChange={(e) => setFormData(prev => ({ ...prev, balance: e.target.value }))}
              placeholder="0.00"
              disabled={loading}
              className="h-10 border-slate-300"
            />
            <p className="text-xs text-gray-500">Enter the current balance of the account</p>
          </div>

          {/* Account Type and Currency */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="accountType">Account Type (Optional)</Label>
              <Select
                value={formData.accountType}
                onValueChange={(value) => setFormData(prev => ({ ...prev, accountType: value }))}
                disabled={loading}
              >
                <SelectTrigger id="accountType" className="h-10 border-slate-300 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="checking">Checking</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                  <SelectItem value="business_checking">Business Checking</SelectItem>
                  <SelectItem value="business_savings">Business Savings</SelectItem>
                  <SelectItem value="credit">Credit</SelectItem>
                  <SelectItem value="money_market">Money Market</SelectItem>
                  <SelectItem value="line_of_credit">Line of Credit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency">Currency (Optional)</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}
                disabled={loading}
              >
                <SelectTrigger id="currency" className="h-10 border-slate-300 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RWF">RWF (Rwandan Franc)</SelectItem>
                  <SelectItem value="USD">USD (US Dollar)</SelectItem>
                  <SelectItem value="EUR">EUR (Euro)</SelectItem>
                  <SelectItem value="GBP">GBP (British Pound)</SelectItem>
                  <SelectItem value="KES">KES (Kenyan Shilling)</SelectItem>
                  <SelectItem value="UGX">UGX (Ugandan Shilling)</SelectItem>
                  <SelectItem value="TZS">TZS (Tanzanian Shilling)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Routing Number and SWIFT Code */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="routingNumber">Routing Number (Optional)</Label>
              <Input
                id="routingNumber"
                value={formData.routingNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, routingNumber: e.target.value }))}
                placeholder="Enter routing number"
                disabled={loading}
                className="h-10 border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="swiftCode">SWIFT Code (Optional)</Label>
              <Input
                id="swiftCode"
                value={formData.swiftCode}
                onChange={(e) => setFormData(prev => ({ ...prev, swiftCode: e.target.value }))}
                placeholder="Enter SWIFT code"
                disabled={loading}
                className="h-10 border-slate-300"
              />
            </div>
          </div>

          {/* IBAN and Account Holder */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="iban">IBAN (Optional)</Label>
              <Input
                id="iban"
                value={formData.iban}
                onChange={(e) => setFormData(prev => ({ ...prev, iban: e.target.value }))}
                placeholder="Enter IBAN"
                disabled={loading}
                className="h-10 border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="accountHolder">Account Holder (Optional)</Label>
              <Input
                id="accountHolder"
                value={formData.accountHolder}
                onChange={(e) => setFormData(prev => ({ ...prev, accountHolder: e.target.value }))}
                placeholder="Enter account holder name"
                disabled={loading}
                className="h-10 border-slate-300"
              />
            </div>
          </div>

          {/* Branch Code and Branch Name */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="branchCode">Branch Code (Optional)</Label>
              <Input
                id="branchCode"
                value={formData.branchCode}
                onChange={(e) => setFormData(prev => ({ ...prev, branchCode: e.target.value }))}
                placeholder="Enter branch code"
                disabled={loading}
                className="h-10 border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="branchName">Branch Name (Optional)</Label>
              <Input
                id="branchName"
                value={formData.branchName}
                onChange={(e) => setFormData(prev => ({ ...prev, branchName: e.target.value }))}
                placeholder="Enter branch name"
                disabled={loading}
                className="h-10 border-slate-300"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes about this account"
              disabled={loading}
              rows={3}
              className="border-slate-300"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              resetForm();
              onOpenChange(false);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              editingAccount ? 'Update Account' : 'Create Account'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

